import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FEATURES, CLIENTS } from '../../data/landing';

const SLIDES = [
  {
    id: 1,
    bg: 'linear-gradient(160deg, #FFF8F8 0%, #FDE8E8 100%)',
    badge: 'Trusted in Hyderabad since 2026',
    badgeBg: '#FDE8E8',
    badgeColor: '#C8171A',
    headline: 'Pure Water,<br/>Delivered Daily',
    sub: 'Vijaya Varshi Water Plant brings safe, purified drinking water straight to your doorstep. Serving homes &amp; businesses across Hyderabad.',
    cta1: { to: '/contact', label: 'Order Now &#8594;' },
    cta2: { to: '/products', label: 'Our Products' },
  },
  {
    id: 2,
    bg: 'linear-gradient(160deg, #FFFDF0 0%, #FFF5C2 100%)',
    badge: 'Best Seller',
    badgeBg: '#FFF5C2',
    badgeColor: '#9A6B00',
    headline: '20L Jar &#8212;<br/>Most Economical',
    sub: 'Just &#x20B9;60 per refill. Our flagship 20L food-grade refillable jar is perfect for families and offices. One-time &#x20B9;150 deposit, fully refundable.',
    cta1: { to: '/products/20l-jar', label: 'View Details &#8594;' },
    cta2: { to: '/contact', label: 'Order Now' },
  },
  {
    id: 3,
    bg: 'linear-gradient(160deg, #ecfeff 0%, #a5f3fc 60%, #cffafe 100%)',
    badge: 'Lab Tested &amp; Certified',
    badgeBg: '#ccfbf1',
    badgeColor: '#0d9488',
    headline: '7-Stage<br/>Purification',
    sub: 'RO + UV treatment, mineral balancing and third-party lab testing every batch. Drink with complete confidence.',
    cta1: { to: '/about', label: 'Learn More &#8594;' },
    cta2: { to: '/products', label: 'See Products' },
  },
  {
    id: 4,
    bg: 'linear-gradient(160deg, #f0fdf4 0%, #bbf7d0 60%, #dcfce7 100%)',
    badge: 'Same-Day Delivery',
    badgeBg: '#dcfce7',
    badgeColor: '#16a34a',
    headline: 'Doorstep Delivery<br/>Every Single Day',
    sub: 'Order by 10 AM and get same-day delivery across Hyderabad. 365 days a year, rain or shine &#8212; reliability you can count on.',
    cta1: { to: '/contact', label: 'Start Delivery &#8594;' },
    cta2: { to: '/about', label: 'About Us' },
  },
];

const features = FEATURES;

const products = [
  { slug: '20l-jar', name: '20L Jar', price: 60, deposit: 150, icon: '&#x1FAA3;', img: '/products/jar-20l.png?v=2', popular: true, sub: 'Vijaya Varshi', capacity: '20 Litres', headline: 'The family favourite', desc: 'Our flagship 20-litre refillable jar is built for everyday home and office use. Made from food-grade material and sealed fresh at our facility. At just ₹60 per refill, it is the most economical way to keep your family hydrated every single day.' },
  { slug: '1l-bottle', name: '1L Bottle', price: 20, deposit: null, icon: '&#x1F376;', img: '/products/bottle-1l.png?v=2', popular: false, sub: 'Vijaya Varshi', capacity: '1 Litre', headline: 'Pure water on the go', desc: 'Crisp, clean and convenient. Our 1-litre bottle is the ideal companion for travel, the gym or a day out. Same 7-stage purification, same trusted quality — in a format that fits your bag.' },
  { slug: '500ml-bottle', name: '500ml Bottle', price: 10, deposit: null, icon: '&#x1FAD9;', img: '/products/bottle-500ml.png?v=2', popular: false, sub: 'Vijaya Varshi', capacity: '500 ml', headline: 'Pocket-size purity', desc: 'Compact and refreshing. Our 500ml bottle is perfect for a quick hydration top-up at school, work or on the move. BIS certified and lab tested for complete peace of mind.' },
  { slug: '250ml-bottle', name: '250ml Bottle', price: 6, deposit: null, icon: '&#x1F4A7;', img: '/products/bottle-250ml.png?v=2', popular: false, sub: 'Vijaya Varshi', capacity: '250 ml', headline: 'Sip & go', desc: 'The perfect single-serve size for events, hospitality and gifting. Our 250ml bottle delivers the same high purity in one clean sip — ideal for weddings, conferences and catered functions.' },
];

const steps = [
  { num: '01', title: 'Place Your Order', desc: 'Call, WhatsApp or contact us online. We confirm your order in minutes.' },
  { num: '02', title: 'We Purify & Pack', desc: 'Water is purified, lab-tested and hygienically packed at our facility.' },
  { num: '03', title: 'Doorstep Delivery', desc: 'Our delivery team brings it straight to your door on the same day.' },
  { num: '04', title: 'Enjoy Pure Water', desc: 'Drink clean, safe water with complete confidence every day.' },
];

// CLIENTS imported from shared data/landing.js

function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [heroImages, setHeroImages] = useState([]);

  useEffect(() => {
    fetch('/api/gallery')
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setHeroImages(data.slice(0, 4)); })
      .catch(() => {});
  }, []);

  // Merge gallery images into slides (up to 3); fall back to gradient if no image
  const slides = SLIDES.map((s, i) => ({ ...s, heroImage: heroImages[i]?.image_path || null }));

  const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 4800);
    return () => clearInterval(t);
  }, [next, paused]);

  return (
    <div
      className="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="slider-track" style={{ transform: 'translateX(-' + current * 100 + '%)' }}>
        {slides.map(slide => {
          const hasImg = !!slide.heroImage;
          return (
            <div
              key={slide.id}
              className="slide"
              style={hasImg ? {
                backgroundImage: `url(${slide.heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
              } : { background: slide.bg }}
            >
              {/* Dark gradient overlay for image slides */}
              {hasImg && (
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(12,26,46,0.72) 0%, rgba(200,23,26,0.45) 60%, rgba(12,26,46,0.55) 100%)', zIndex: 0 }} />
              )}
              <div className="slide-content" style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  background: hasImg ? 'rgba(255,255,255,0.18)' : slide.badgeBg,
                  color: hasImg ? 'white' : slide.badgeColor,
                  backdropFilter: hasImg ? 'blur(6px)' : 'none',
                  border: hasImg ? '1px solid rgba(255,255,255,0.3)' : 'none',
                  borderRadius: '9999px', padding: '0.35rem 1rem', fontSize: '0.82rem', fontWeight: 700, marginBottom: '1.25rem',
                }} dangerouslySetInnerHTML={{ __html: slide.badge }} />
                <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, lineHeight: 1.15, marginBottom: '1.1rem', color: hasImg ? 'white' : 'var(--text-dark)', textShadow: hasImg ? '0 2px 16px rgba(0,0,0,0.4)' : 'none' }}
                  dangerouslySetInnerHTML={{ __html: slide.headline }} />
                <p style={{ fontSize: '1.05rem', color: hasImg ? 'rgba(255,255,255,0.9)' : 'var(--text-body)', maxWidth: '540px', margin: '0 0 2.25rem', lineHeight: 1.75 }}
                  dangerouslySetInnerHTML={{ __html: slide.sub }} />
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                  <Link to={slide.cta1.to} className="btn-brand btn-brand-lg" dangerouslySetInnerHTML={{ __html: slide.cta1.label }} />
                  <Link
                    to={slide.cta2.to}
                    style={hasImg ? { background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.5)', backdropFilter: 'blur(4px)', padding: '0.75rem 2rem', borderRadius: '10px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' } : {}}
                    className={hasImg ? '' : 'btn-outline btn-outline-lg'}
                  >
                    {slide.cta2.label}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <button className="slider-btn prev" onClick={prev} aria-label="Previous">&#8592;</button>
      <button className="slider-btn next" onClick={next} aria-label="Next">&#8594;</button>
      <div className="slider-dots">
        {slides.map((_, i) => (
          <button key={i} className={'slider-dot' + (i === current ? ' active' : '')} onClick={() => setCurrent(i)} aria-label={'Slide ' + (i + 1)} />
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [activeSlug, setActiveSlug] = useState('20l-jar');

  return (
    <div style={{ background: '#ffffff' }}>
      <HeroSlider />

      {/* Trust Strip */}
      <div className="trust-strip" style={{ background: 'var(--brand)', padding: '0.85rem 1.5rem' }}>
        <div className="page-container" style={{ display: 'flex', justifyContent: 'center', gap: '2.5rem', flexWrap: 'wrap' }}>
          {['7-Stage Purification', 'Lab Tested Every Batch', 'ISI &amp; FSSAI Certified', 'Same Day Delivery'].map(item => (
            <span key={item} style={{ fontSize: '0.8375rem', fontWeight: 600, color: 'white' }} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="section" style={{ background: 'white' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x2728; Why Choose Us</span>
            <h2 className="section-title">Why Choose Vijaya Varshi?</h2>
            <p className="section-subtitle">Quality you can taste &mdash; verified at every step, from source to your home.</p>
          </div>
          <div className="grid grid-3" style={{ gap: '1.25rem' }}>
            {features.map(f => (
              <div key={f.title} className="pub-card feature-card">
                <span className="feature-check" aria-hidden="true">&#10003;</span>
                <div>
                  <h3 className="feature-title">{f.title}</h3>
                  <p className="text-justify feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      {(() => {
        const activeProduct = products.find(p => p.slug === activeSlug);
        const activeIdx = products.findIndex(p => p.slug === activeSlug);
        const nextProduct = products[(activeIdx + 1) % products.length];
        return (
          <section style={{ background: 'linear-gradient(160deg, #FFF8F0 0%, #FFE0CC 30%, #FFDCA0 65%, #FFC300 100%)', padding: '5rem 1.5rem', overflow: 'hidden' }}>
            <div className="page-container">

              {/* Heading */}
              <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                <h2 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: '#3D0808', marginBottom: '0.6rem', lineHeight: 1.2 }}>
                  Our products, your trust
                </h2>
                <p style={{ color: '#7B3A00', fontSize: '1rem', fontWeight: 500 }}>
                  A household name for pure water — from home to office, every size covered.
                </p>
              </div>

              {/* Main layout: tabs + image/card */}
              <div className="home-prods-row">

                {/* Left: elegant tabs */}
                <div className="home-prods-tabs">
                  {products.map(p => {
                    const isActive = p.slug === activeSlug;
                    return (
                      <div
                        key={p.slug}
                        onClick={() => setActiveSlug(p.slug)}
                        style={{
                          cursor: 'pointer',
                          background: isActive ? 'white' : 'rgba(255,255,255,0.55)',
                          borderRadius: '16px',
                          padding: '0.8rem 1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          transition: 'all 0.25s',
                          boxShadow: isActive ? '0 8px 32px rgba(200,23,26,0.18)' : 'none',
                          border: isActive ? '2px solid #C8171A' : '2px solid rgba(200,23,26,0.15)',
                        }}
                      >
                        <div style={{ width: 40, height: 46, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={p.img} alt={p.name} onError={e => { e.target.style.display = 'none'; }}
                            style={{ maxWidth: 36, maxHeight: 44, objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.83rem', fontWeight: 700, color: isActive ? '#0c1a2e' : '#3D0808', lineHeight: 1.2, marginBottom: '0.2rem' }}>
                            {p.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#C8171A' }}>₹{p.price}</span>
                            <span style={{ fontSize: '0.6rem', fontWeight: 700, background: isActive ? '#FEF2F2' : 'rgba(200,23,26,0.08)', color: '#C8171A', borderRadius: '4px', padding: '0.1rem 0.35rem' }}>
                              {p.capacity}
                            </span>
                          </div>
                        </div>
                        {isActive && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#C8171A', flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                </div>

                {/* Right: image + overlapping card */}
                <div className="home-prods-display">

                  {/* Frosted backdrop */}
                  <div className="home-prods-backdrop" style={{ background: 'rgba(255,255,255,0.35)', borderRadius: '28px', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.7)' }} />

                  {/* Large product image */}
                  <div className="home-prods-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    {activeProduct.popular && (
                      <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', background: '#FFC300', color: '#7B0D10', fontSize: '0.67rem', fontWeight: 800, padding: '0.28rem 1rem', borderRadius: '9999px', letterSpacing: '0.06em', whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(0,0,0,0.2)', zIndex: 2 }}>
                        BEST SELLER
                      </div>
                    )}
                    <img
                      key={activeProduct.slug}
                      src={activeProduct.img}
                      alt={activeProduct.name}
                      onError={e => { e.target.style.display = 'none'; }}
                      style={{ height: 400, objectFit: 'contain', borderRadius: 18, filter: 'drop-shadow(0 28px 56px rgba(0,0,0,0.35))' }}
                    />
                  </div>

                  {/* Overlapping details card */}
                  <div className="home-prods-card" style={{ background: 'white', borderRadius: '24px', padding: '1.75rem', boxShadow: '0 24px 80px rgba(0,0,0,0.24)', zIndex: 2 }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.35rem' }}>
                      {activeProduct.sub} · {activeProduct.capacity}
                    </div>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0c1a2e', marginBottom: '0.6rem', lineHeight: 1.2 }}>
                      {activeProduct.headline}
                    </h3>
                    <p className="text-justify" style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.75, marginBottom: '0.9rem' }}>
                      {activeProduct.desc}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ fontSize: '1.65rem', fontWeight: 900, color: '#C8171A' }}>₹{activeProduct.price}</span>
                        <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginLeft: '0.3rem' }}>/ {activeProduct.slug === '20l-jar' ? 'refill' : 'bottle'}</span>
                      </div>
                      {activeProduct.deposit && (
                        <span style={{ fontSize: '0.7rem', color: '#64748b', background: '#f1f5f9', padding: '0.25rem 0.6rem', borderRadius: '6px' }}>
                          ₹{activeProduct.deposit} refundable
                        </span>
                      )}
                    </div>
                    <Link to={'/products/' + activeProduct.slug} style={{ display: 'inline-block', background: '#C8171A', color: 'white', padding: '0.55rem 1.35rem', borderRadius: '9px', fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none' }}>
                      Learn more →
                    </Link>

                    <div style={{ height: '1px', background: '#e2e8f0', margin: '1.1rem 0' }} />

                    {/* Next product teaser */}
                    <div style={{ opacity: 0.5, cursor: 'pointer' }} onClick={() => setActiveSlug(nextProduct.slug)}>
                      <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                        {nextProduct.sub} · {nextProduct.capacity}
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0c1a2e', marginBottom: '0.25rem' }}>{nextProduct.headline}</div>
                      <div style={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5, marginBottom: '0.35rem' }}>
                        {nextProduct.desc.slice(0, 72)}…
                      </div>
                      <span style={{ color: '#C8171A', fontWeight: 700, fontSize: '0.8rem', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                        View {nextProduct.name} →
                      </span>
                    </div>
                  </div>

                </div>
              </div>

              {/* View All CTA */}
              <div style={{ textAlign: 'center', marginTop: '3.5rem' }}>
                <Link to="/products" style={{ background: '#C8171A', color: 'white', padding: '0.85rem 2.5rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.9375rem', textDecoration: 'none', boxShadow: '0 4px 20px rgba(200,23,26,0.35)', display: 'inline-block' }}>
                  View All Products &amp; Pricing →
                </Link>
              </div>

            </div>
          </section>
        );
      })()}

      {/* How It Works */}
      <section className="section" style={{ background: 'white' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x1F504; How It Works</span>
            <h2 className="section-title">From Order to Doorstep</h2>
            <p className="section-subtitle">Getting pure water delivered to your home is simple. Just 4 easy steps.</p>
          </div>
          <div className="grid grid-4" style={{ gap: '1.5rem' }}>
            {steps.map((step, i) => (
              <div key={step.num} style={{ textAlign: 'center', position: 'relative' }}>
                {i < steps.length - 1 && (
                  <div className="step-connector" style={{ position: 'absolute', top: 30, left: 'calc(50% + 38px)', right: 0, height: '2px', background: 'var(--brand-light)' }} />
                )}
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--brand)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.1rem', fontWeight: 800 }}>
                  {step.num}
                </div>
                <div style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--brand)', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>STEP {step.num}</div>
                <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', marginBottom: '0.4rem', fontSize: '0.9375rem' }}>{step.title}</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.84rem', lineHeight: 1.65, textAlign: 'center' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Clients */}
      <section className="clients-section" style={{ background: 'var(--brand-xlight)', padding: '3.5rem 1.5rem', borderTop: '1px solid var(--brand-light)', borderBottom: '1px solid var(--brand-light)' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <span className="section-badge">Trusted Partnerships</span>
            <h2 className="section-title" style={{ marginBottom: '0.4rem' }}>Our Clients</h2>
            <p className="section-subtitle" style={{ marginBottom: 0 }}>
              Proudly serving leading enterprises, banks and government institutions across India.
            </p>
          </div>
          <div className="clients-marquee">
            <div className="clients-track">
              {[...CLIENTS, ...CLIENTS].map((c, i) => (
                <div key={i} className="client-logo" title={c.name}>
                  {c.logo && (
                    <img
                      src={c.logo}
                      alt={c.name}
                      onError={e => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                  )}
                  <span className="client-logo-text" style={{ display: c.logo ? 'none' : 'block' }}>
                    {c.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--brand)', padding: '4rem 1.5rem', textAlign: 'center' }}>
        <div className="page-container" style={{ maxWidth: '620px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
            Start Getting Pure Water Today
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '2rem', lineHeight: 1.7, fontSize: '1rem' }}>
            Join 1000+ happy customers in Hyderabad who trust Vijaya Varshi for their daily drinking water needs.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ background: 'white', color: 'var(--brand)', padding: '0.85rem 2.25rem', borderRadius: '10px', fontWeight: 700, fontSize: '1.0625rem', textDecoration: 'none' }}>
              Contact Us to Order
            </Link>
            <a href="tel:8374422411" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.5)', padding: '0.85rem 2.25rem', borderRadius: '10px', fontWeight: 700, fontSize: '1.0625rem', textDecoration: 'none' }}>
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
